import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  Select,
  Table,
  TableFooter,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AccessTime,
  CalendarMonth,
  Cancel,
  Close,
  Delete,
  History,
  LocationOn,
  PictureAsPdf,
  Send,
  Videocam,
  ExpandMore,
} from "@mui/icons-material";
import * as moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDEditor from "components/MDEditor";
import ApiCalls from "services/flick_erp_api_call";
import { interviewsList } from "./interviewsSlice";
import { setToastOpen } from "components/Toast/toastAlertSlice";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";

const statusOptions = [
  { value: 1, label: "Scheduled" },
  { value: 2, label: "Selected" },
  { value: 3, label: "Rejected" },
  { value: 4, label: "Rescheduled" },
  { value: 5, label: "Lost" },
];

const feedbackStatuses = [
  { value: 2, label: "Selected" },
  { value: 3, label: "Rejected" },
];

const getScheduleStatusChip = (scheduleStatus) => {
  const normalizedStatus = String(scheduleStatus || "scheduled").toLowerCase();
  const chipMap = {
    scheduled: { label: "Scheduled", color: "info" },
    rescheduled: { label: "Rescheduled", color: "warning" },
    cancelled: { label: "Cancelled", color: "error" },
    no_show: { label: "No Show", color: "default" },
    noshow: { label: "No Show", color: "default" },
    "no-show": { label: "No Show", color: "default" },
    completed: { label: "Completed", color: "success" },
  };

  return chipMap[normalizedStatus] || { label: scheduleStatus || "Scheduled", color: "info" };
};

const getStatusLabel = (value) => {
  const match = statusOptions.find((item) => Number(item.value) === Number(value));
  return match?.label || "Unknown";
};

const getInterviewerName = (employees, interviewerIds) => {
  const firstInterviewerId = Array.isArray(interviewerIds) ? interviewerIds[0] : interviewerIds;
  const match = employees.find((employee) => employee?.id === firstInterviewerId);
  if (!match) {
    return "Not assigned";
  }

  return `${match?.user?.firstname || ""} ${match?.user?.lastname || ""}`.trim();
};

const normalizeHistoryItems = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.history)) {
    return data.history;
  }

  return [];
};

const formatDateTimeValue = (value) => {
  if (!value) {
    return "";
  }

  return moment(value).format("YYYY-MM-DDTHH:mm");
};

const buildInterviewContext = (interviewRecord, employees) => ({
  interviewerLabel: getInterviewerName(employees, interviewRecord?.interviewer),
  candidateName: interviewRecord?.job_opening?.candidate?.full_name || "Candidate",
  flowName: interviewRecord?.job_opening?.current_flow?.name || "Flow not available",
  stageName: interviewRecord?.job_opening?.current_stage?.name || "Stage not available",
});

function Interviews({ permissions, modulePermissions }) {
  const { size } = typography;
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const interviews = useSelector((state) => state.interviews.interviews);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [employees, setEmployees] = useState([]);
  const [interviewList, setInterviewList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortStatus, setSortStatus] = useState(statusOptions[0]);

  const [feedbackDialog, setFeedbackDialog] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [selectedInterviewRecord, setSelectedInterviewRecord] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({
    interviewer: "",
    status: 2,
    rating: 0,
    evaluated_exp: "",
    feedback: "",
  });

  const [rescheduleDialog, setRescheduleDialog] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    datetime: "",
    mode: "online",
    meeting_link: "",
    address: "",
    timezone: "Asia/Kolkata",
    meeting_note: "",
    reason: "",
  });
  const [rescheduleError, setRescheduleError] = useState("");

  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");

  const [noShowDialog, setNoShowDialog] = useState(false);
  const [noShowReason, setNoShowReason] = useState("");
  const [noShowError, setNoShowError] = useState("");

  const [historyDialog, setHistoryDialog] = useState(false);
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const baseURL = "http://api.flickerp.com/";

  useEffect(() => {
    getEmployee();
  }, []);

  useEffect(() => {
    if (sortStatus?.value) {
      getInterviewsList(`?status=${sortStatus.value}`);
    } else {
      getInterviewsList();
    }
  }, [sortStatus]);

  async function getEmployee() {
    const res = await ApiCalls.getEmployeesList("?status=active");
    if (res.success && res.code === 200) {
      setEmployees(res.data || []);
    }
  }

  async function getInterviewsList(queryParams) {
    const res = await ApiCalls.getInterviewsList(queryParams);
    if (res.success && res.code === 200) {
      dispatch(interviewsList({ interviews: res.data }));
      setInterviewList(res.data || []);
    }
  }

  const visibleInterviews = useMemo(() => {
    const normalizedQuery = searchText.trim().toLowerCase();
    const sourceList = Array.isArray(interviews) && interviews.length ? interviews : interviewList;

    return sourceList
      .filter((row) => {
        const interviewerIds = Array.isArray(row?.interviewer) ? row.interviewer : [row?.interviewer];
        return interviewerIds.includes(storedUser?.employee_id);
      })
      .filter((row) => {
        if (!normalizedQuery) {
          return true;
        }

        const candidateName = row?.job_opening?.candidate?.full_name?.toLowerCase() || "";
        const interviewerName = getInterviewerName(employees, row?.interviewer).toLowerCase();
        const currentFlow = row?.job_opening?.current_flow?.name?.toLowerCase() || "";
        const currentStage = row?.job_opening?.current_stage?.name?.toLowerCase() || "";

        return (
          candidateName.includes(normalizedQuery) ||
          interviewerName.includes(normalizedQuery) ||
          currentFlow.includes(normalizedQuery) ||
          currentStage.includes(normalizedQuery)
        );
      });
  }, [employees, interviewList, interviews, searchText, storedUser?.employee_id]);

  const paginatedInterviews =
    rowsPerPage > 0
      ? visibleInterviews.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : visibleInterviews;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const closeFeedbackDialog = () => {
    setFeedbackDialog(false);
    setSelectedInterviewRecord(null);
    setFeedbackError("");
    setFeedbackForm({
      interviewer: "",
      status: 2,
      rating: 0,
      evaluated_exp: "",
      feedback: "",
    });
  };

  const handleOpenFeedback = (interviewRecord) => {
    if (String(interviewRecord?.schedule_status || "").toLowerCase() === "cancelled") {
      dispatch(
        setToastOpen({
          isOpen: true,
          message: "Feedback cannot be submitted for cancelled interviews.",
        })
      );
      return;
    }

    const interviewerId = Array.isArray(interviewRecord?.interviewer)
      ? interviewRecord.interviewer[0]
      : interviewRecord?.interviewer;

    setSelectedInterviewRecord(interviewRecord);
    setFeedbackForm({
      interviewer: interviewerId || "",
      status: 2,
      rating: 0,
      evaluated_exp: "",
      feedback: "",
    });
    setFeedbackError("");
    setFeedbackDialog(true);
  };

  const handleSubmitFeedback = async () => {
    const payload = {
      interviewer: Number(feedbackForm.interviewer),
      status: Number(feedbackForm.status),
      rating: Number(feedbackForm.rating),
      evaluated_exp: Number(feedbackForm.evaluated_exp),
      feedback: feedbackForm.feedback,
    };

    if (!payload.interviewer) {
      setFeedbackError("Interviewer is required.");
      return;
    }

    if (![2, 3].includes(payload.status)) {
      setFeedbackError("Status must be Selected or Rejected.");
      return;
    }

    if (!payload.rating) {
      setFeedbackError("Rating is required.");
      return;
    }

    if (feedbackForm.evaluated_exp === "" || Number.isNaN(payload.evaluated_exp)) {
      setFeedbackError("Evaluated experience is required.");
      return;
    }

    if (!payload.feedback?.trim()) {
      setFeedbackError("Feedback is required.");
      return;
    }

    const res = await ApiCalls.submitInterviewFeedback(selectedInterviewRecord?.id, payload);
    if (res?.success && res?.code === 201) {
      closeFeedbackDialog();
      getInterviewsList(`?status=${sortStatus?.value}`);
    }
  };

  const closeRescheduleDialog = () => {
    setRescheduleDialog(false);
    setRescheduleError("");
    setSelectedInterviewRecord(null);
    setRescheduleForm({
      datetime: "",
      mode: "online",
      meeting_link: "",
      address: "",
      timezone: "Asia/Kolkata",
      meeting_note: "",
      reason: "",
    });
  };

  const handleOpenReschedule = (interviewRecord) => {
    setSelectedInterviewRecord(interviewRecord);
    setRescheduleForm({
      datetime: formatDateTimeValue(interviewRecord?.datetime),
      mode: interviewRecord?.mode || "online",
      meeting_link: interviewRecord?.meeting_link || "",
      address: interviewRecord?.address || "",
      timezone: interviewRecord?.timezone || "Asia/Kolkata",
      meeting_note: interviewRecord?.meeting_note || "",
      reason: "",
    });
    setRescheduleError("");
    setRescheduleDialog(true);
  };

  const handleSubmitReschedule = async () => {
    if (!rescheduleForm.datetime) {
      setRescheduleError("Interview date and time are required.");
      return;
    }

    if (!rescheduleForm.reason.trim()) {
      setRescheduleError("Reason is required for rescheduling.");
      return;
    }

    if (rescheduleForm.mode === "online" && !rescheduleForm.meeting_link.trim()) {
      setRescheduleError("Meeting link is required for online interviews.");
      return;
    }

    if (rescheduleForm.mode === "offline" && !rescheduleForm.address.trim()) {
      setRescheduleError("Location is required for offline interviews.");
      return;
    }

    const payload = {
      datetime: new Date(rescheduleForm.datetime).toISOString(),
      mode: rescheduleForm.mode,
      timezone: rescheduleForm.timezone,
      meeting_note: rescheduleForm.meeting_note,
      reason: rescheduleForm.reason,
    };

    if (rescheduleForm.mode === "online") {
      payload.meeting_link = rescheduleForm.meeting_link;
    } else {
      payload.address = rescheduleForm.address;
    }

    const res = await ApiCalls.rescheduleInterview(selectedInterviewRecord?.id, payload);
    if (res?.success && (res?.code === 200 || res?.code === 201)) {
      closeRescheduleDialog();
      getInterviewsList(`?status=${sortStatus?.value}`);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialog(false);
    setCancelReason("");
    setCancelError("");
    setSelectedInterviewRecord(null);
  };

  const handleOpenCancel = (interviewRecord) => {
    setSelectedInterviewRecord(interviewRecord);
    setCancelReason("");
    setCancelError("");
    setCancelDialog(true);
  };

  const handleSubmitCancel = async () => {
    if (!cancelReason.trim()) {
      setCancelError("Reason is required to cancel an interview.");
      return;
    }

    const res = await ApiCalls.cancelInterviewSchedule(selectedInterviewRecord?.id, {
      reason: cancelReason,
    });
    if (res?.success && (res?.code === 200 || res?.code === 201)) {
      closeCancelDialog();
      getInterviewsList(`?status=${sortStatus?.value}`);
    }
  };

  const closeNoShowDialog = () => {
    setNoShowDialog(false);
    setNoShowReason("");
    setNoShowError("");
    setSelectedInterviewRecord(null);
  };

  const handleOpenNoShow = (interviewRecord) => {
    setSelectedInterviewRecord(interviewRecord);
    setNoShowReason("");
    setNoShowError("");
    setNoShowDialog(true);
  };

  const handleSubmitNoShow = async () => {
    if (!noShowReason.trim()) {
      setNoShowError("Reason is required to mark an interview as no-show.");
      return;
    }

    const res = await ApiCalls.markInterviewNoShow(selectedInterviewRecord?.id, {
      reason: noShowReason,
    });
    if (res?.success && (res?.code === 200 || res?.code === 201)) {
      closeNoShowDialog();
      getInterviewsList(`?status=${sortStatus?.value}`);
    }
  };

  const closeHistoryDialog = () => {
    setHistoryDialog(false);
    setScheduleHistory([]);
    setSelectedInterviewRecord(null);
  };

  const handleOpenHistory = async (interviewRecord) => {
    setSelectedInterviewRecord(interviewRecord);
    setHistoryDialog(true);
    setHistoryLoading(true);
    const res = await ApiCalls.getInterviewScheduleHistory(interviewRecord?.id);
    if (res?.success && res?.code === 200) {
      setScheduleHistory(normalizeHistoryItems(res?.data));
    } else {
      setScheduleHistory([]);
    }
    setHistoryLoading(false);
  };

  const handleDeleteClick = async (interviewRecord) => {
    const res = await ApiCalls.deleteInterview(interviewRecord.id);
    if (res?.success && res?.code === 204) {
      getInterviewsList(`?status=${sortStatus?.value}`);
    }
  };

  const selectedInterviewContext = buildInterviewContext(selectedInterviewRecord, employees);
  const isDarkMode = theme.palette.mode === "dark";
  const surfaceColor = isDarkMode ? theme.palette.background.paper : "#fff";
  const mutedSurfaceColor = isDarkMode ? theme.palette.background.default : colors.background.default;
  const borderColor = isDarkMode ? theme.palette.divider : colors.grey[300];
  const detailCardSx = {
    p: 2,
    borderRadius: "18px",
    background: isDarkMode
      ? "linear-gradient(180deg, rgba(30,41,59,0.96) 0%, rgba(15,23,42,0.92) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(244,247,255,1) 100%)",
    border: `1px solid ${borderColor}`,
    boxShadow: isDarkMode ? "0 12px 28px rgba(2, 6, 23, 0.2)" : "0 12px 26px rgba(15, 23, 42, 0.05)",
  };

  return (
    <>
      <MDBox>
        <Card
          sx={{
            mb: 2.5,
            p: { xs: 2, md: 2.5 },
            borderRadius: "28px",
            boxShadow: "none",
            border: `1px solid ${borderColor}`,
            background: isDarkMode
              ? "linear-gradient(160deg, rgba(30,41,59,0.96), rgba(15,23,42,0.9))"
              : "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(245,247,255,0.98))",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isSmallScreen ? "stretch" : "center",
              gap: 2,
            }}
          >
          <Box>
          <MDTypography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.04em" }}>
            Interviews
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ mt: 0.8, maxWidth: 640, lineHeight: 1.7 }}>
            Track scheduled rounds, capture reschedules and no-shows, and close interviewer feedback with a clearer review surface.
          </MDTypography>
          </Box>
          <MDBox
            width={isSmallScreen ? "100%" : "auto"}
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            alignItems="center"
            py={1}
            px={1}
            gap={1}
            color="text"
            fontSize={size.sm}
          >
            <TextField
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(0);
              }}
              placeholder="Search candidate, flow, stage"
              size="small"
              sx={{
                width: isSmallScreen ? "100%" : 320,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  backgroundColor: surfaceColor,
                },
              }}
            />
            <Autocomplete
              value={sortStatus}
              options={statusOptions}
              onChange={(event, newValue) => {
                setSortStatus(newValue || statusOptions[0]);
                setPage(0);
              }}
              sx={{
                width: isSmallScreen ? "100%" : 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  backgroundColor: surfaceColor,
                },
              }}
              renderInput={(params) => <TextField {...params} label="Interview Status" size="small" />}
            />
          </MDBox>
          </Box>
        </Card>

        {visibleInterviews.length ? (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {paginatedInterviews.map((row) => {
              const scheduleChip = getScheduleStatusChip(row?.schedule_status);
              const isCancelled = String(row?.schedule_status || "").toLowerCase() === "cancelled";

              return (
                <Accordion
                  key={row.id}
                  disableGutters
                  sx={{
                    borderRadius: "18px !important",
                    overflow: "hidden",
                    border: `1px solid ${borderColor}`,
                    boxShadow: "none",
                    backgroundColor: surfaceColor,
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      backgroundColor: surfaceColor,
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                      },
                    }}
                  >
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection={isSmallScreen ? "column" : "row"}
                      justifyContent="space-between"
                      alignItems={isSmallScreen ? "flex-start" : "center"}
                      gap={1.5}
                    >
                      <Box>
                        <Typography fontWeight={700} fontSize="16px">
                          {row?.job_opening?.candidate?.full_name || "Candidate"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {getInterviewerName(employees, row?.interviewer)} • {row?.datetime ? moment(row.datetime).format("DD MMM YYYY, hh:mm A") : "Not scheduled"}
                        </Typography>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                        <Chip label={getStatusLabel(row?.status)} size="small" variant="outlined" />
                        <Chip label={scheduleChip.label} color={scheduleChip.color} size="small" />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, py: 2, backgroundColor: mutedSurfaceColor }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={7}>
                        <Box sx={detailCardSx}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5} mb={1.75}>
                            <Box>
                              <Typography fontWeight={700} fontSize="1rem">
                                Round Summary
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                                Review the exact stage, schedule mode, and meeting context before taking action.
                              </Typography>
                            </Box>
                            <Chip
                              label={row?.mode === "offline" ? "Offline" : "Online"}
                              size="small"
                              icon={row?.mode === "offline" ? <LocationOn /> : <Videocam />}
                              sx={{ textTransform: "capitalize" }}
                            />
                          </Box>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ p: 1.5, borderRadius: "14px", backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(103,80,164,0.05)" }}>
                                <Typography variant="caption" color="text.secondary">Current Flow</Typography>
                                <Typography fontWeight={700}>{row?.job_opening?.current_flow?.name || "Not Found"}</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ p: 1.5, borderRadius: "14px", backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(103,80,164,0.05)" }}>
                                <Typography variant="caption" color="text.secondary">Current Stage</Typography>
                                <Typography fontWeight={700}>{row?.job_opening?.current_stage?.name || "Not Found"}</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                                <CalendarMonth sx={{ color: colors.info.main, mt: 0.2 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Scheduled Date</Typography>
                                  <Typography fontWeight={600}>
                                    {row?.datetime ? moment(row.datetime).format("DD MMM YYYY") : "Not scheduled"}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                                <AccessTime sx={{ color: colors.warning.main, mt: 0.2 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Time & Zone</Typography>
                                  <Typography fontWeight={600}>
                                    {row?.datetime ? moment(row.datetime).format("hh:mm A") : "Pending"} • {row?.timezone || "Asia/Kolkata"}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                          <Box
                            mt={2}
                            p={1.5}
                            sx={{
                              borderRadius: "14px",
                              backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.025)",
                              border: `1px dashed ${borderColor}`,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {row?.mode === "offline" ? "Interview location" : "Meeting link"}
                            </Typography>
                            <Typography fontWeight={600} sx={{ wordBreak: "break-word", mt: 0.4 }}>
                              {row?.mode === "offline"
                                ? row?.address || "Location pending"
                                : row?.meeting_link || "Meeting link pending"}
                            </Typography>
                            {row?.meeting_note ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Note: {row.meeting_note}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} lg={5}>
                        <Box sx={detailCardSx}>
                          <Typography fontWeight={700} mb={1.5}>
                            Candidate Pack
                          </Typography>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: "14px",
                              backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(103,80,164,0.05)",
                              mb: 1.5,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">Candidate</Typography>
                            <Typography fontWeight={700}>
                              {row?.job_opening?.candidate?.full_name || "Candidate"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {getInterviewerName(employees, row?.interviewer)}
                            </Typography>
                          </Box>
                          {row?.job_opening?.candidate?.cv ? (
                            <Tooltip title="Open resume">
                              <a
                                aria-label="open resume"
                                href={`${baseURL}${row?.job_opening?.candidate?.cv}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                <Typography color="inherit" fontWeight={600}>
                                  <PictureAsPdf sx={{ fontSize: "18px", mr: 0.5, mb: "-3px" }} />
                                  Open Resume
                                </Typography>
                              </a>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Resume not available.
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "18px",
                            backgroundColor: surfaceColor,
                            border: `1px solid ${borderColor}`,
                          }}
                        >
                        <Typography fontWeight={700} mb={1.25}>
                          Quick Actions
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={() => handleOpenFeedback(row)}
                            disabled={isCancelled}
                          >
                            Feedback
                          </MDButton>
                          <MDButton size="small" variant="outlined" color="warning" onClick={() => handleOpenReschedule(row)}>
                            Reschedule
                          </MDButton>
                          <MDButton size="small" variant="outlined" color="error" onClick={() => handleOpenCancel(row)}>
                            Cancel
                          </MDButton>
                          <MDButton size="small" variant="outlined" color="dark" onClick={() => handleOpenNoShow(row)}>
                            No-Show
                          </MDButton>
                          <MDButton size="small" variant="outlined" color="secondary" onClick={() => handleOpenHistory(row)}>
                            History
                          </MDButton>
                          {permissions?.includes(modulePermissions?.delete_permission) ? (
                            <MDButton size="small" variant="outlined" color="error" onClick={() => handleDeleteClick(row)}>
                              Delete
                            </MDButton>
                          ) : null}
                        </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            <Paper sx={{ borderRadius: "18px", overflow: "hidden", backgroundColor: surfaceColor }}>
              <Table>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                      colSpan={9}
                      count={visibleInterviews.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      slotProps={{
                        select: { "aria-label": "rows per page" },
                        actions: { showFirstButton: true, showLastButton: true },
                      }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </Paper>
          </Box>
        ) : (
          <Grid item xs={12}>
            <Card
              sx={{
                p: 5,
                pt: 7,
                borderRadius: "24px",
                textAlign: "center",
                border: `1px dashed ${colors.secondary.main}40`,
                backgroundColor: mutedSurfaceColor,
              }}
            >
              <MDTypography variant="h5" sx={{ color: isDarkMode ? colors.light.main : colors.dark.main, mb: 1 }}>
                No interviews are planned for now.
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Schedule an interview from a candidate or job opening to begin the round lifecycle.
              </MDTypography>
            </Card>
          </Grid>
        )}
      </MDBox>

      <Dialog
        open={rescheduleDialog}
        onClose={closeRescheduleDialog}
        PaperProps={{
          sx: {
            width: "640px",
            maxWidth: "94vw",
            borderRadius: "22px",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.warning.main} 0%, ${colors.info.main} 100%)`,
            color: "white",
          }}
        >
          <Typography variant="h5" color="white">
            Reschedule Interview
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.86)" }}>
            Keep the same interview record and capture the reason for the schedule change.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={detailCardSx} mb={2}>
            <Typography fontWeight={700}>{selectedInterviewContext.candidateName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedInterviewContext.flowName} / {selectedInterviewContext.stageName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Interviewer: {selectedInterviewContext.interviewerLabel}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Interview Date & Time"
                InputLabelProps={{ shrink: true }}
                value={rescheduleForm.datetime}
                onChange={(event) => setRescheduleForm((state) => ({ ...state, datetime: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="reschedule-mode-label">Interview Mode</InputLabel>
                <Select
                  labelId="reschedule-mode-label"
                  label="Interview Mode"
                  value={rescheduleForm.mode}
                  onChange={(event) =>
                    setRescheduleForm((state) => ({
                      ...state,
                      mode: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timezone"
                value={rescheduleForm.timezone}
                onChange={(event) => setRescheduleForm((state) => ({ ...state, timezone: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              {rescheduleForm.mode === "offline" ? (
                <TextField
                  fullWidth
                  label="Location"
                  value={rescheduleForm.address}
                  onChange={(event) => setRescheduleForm((state) => ({ ...state, address: event.target.value }))}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Meeting Link"
                  value={rescheduleForm.meeting_link}
                  onChange={(event) =>
                    setRescheduleForm((state) => ({ ...state, meeting_link: event.target.value }))
                  }
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meeting Note"
                value={rescheduleForm.meeting_note}
                onChange={(event) =>
                  setRescheduleForm((state) => ({ ...state, meeting_note: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                minRows={3}
                value={rescheduleForm.reason}
                onChange={(event) => setRescheduleForm((state) => ({ ...state, reason: event.target.value }))}
                helperText="Required. This reason is used for schedule history and notification context."
              />
            </Grid>
          </Grid>
          {rescheduleError ? (
            <Typography color="error" fontSize="14px" mt={2}>
              {rescheduleError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton variant="outlined" color="dark" onClick={closeRescheduleDialog}>
            Close
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleSubmitReschedule}>
            Save Reschedule
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialog}
        onClose={closeCancelDialog}
        PaperProps={{ sx: { width: "540px", maxWidth: "94vw", borderRadius: "22px" } }}
      >
        <DialogTitle>
          <Typography variant="h5">Cancel Interview</Typography>
          <Typography variant="body2" color="text.secondary">
            Cancelling keeps the candidate in the current stage and records the cancellation reason.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={detailCardSx} mb={2}>
            <Typography fontWeight={700}>{selectedInterviewContext.candidateName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedInterviewContext.flowName} / {selectedInterviewContext.stageName}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason"
            value={cancelReason}
            onChange={(event) => {
              setCancelReason(event.target.value);
              setCancelError("");
            }}
          />
          {cancelError ? (
            <Typography color="error" fontSize="14px" mt={2}>
              {cancelError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton variant="outlined" color="dark" onClick={closeCancelDialog}>
            Close
          </MDButton>
          <MDButton variant="contained" color="error" onClick={handleSubmitCancel}>
            Confirm Cancel
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noShowDialog}
        onClose={closeNoShowDialog}
        PaperProps={{ sx: { width: "540px", maxWidth: "94vw", borderRadius: "22px" } }}
      >
        <DialogTitle>
          <Typography variant="h5">Mark No-Show</Typography>
          <Typography variant="body2" color="text.secondary">
            Record when the candidate does not join or attend the interview.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={detailCardSx} mb={2}>
            <Typography fontWeight={700}>{selectedInterviewContext.candidateName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedInterviewContext.flowName} / {selectedInterviewContext.stageName}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason"
            value={noShowReason}
            onChange={(event) => {
              setNoShowReason(event.target.value);
              setNoShowError("");
            }}
          />
          {noShowError ? (
            <Typography color="error" fontSize="14px" mt={2}>
              {noShowError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton variant="outlined" color="dark" onClick={closeNoShowDialog}>
            Close
          </MDButton>
          <MDButton variant="contained" color="dark" onClick={handleSubmitNoShow}>
            Mark No-Show
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyDialog}
        onClose={closeHistoryDialog}
        PaperProps={{ sx: { width: "700px", maxWidth: "95vw", borderRadius: "22px" } }}
      >
        <DialogTitle>
          <Typography variant="h5">Schedule History</Typography>
          <Typography variant="body2" color="text.secondary">
            Review all interview schedule changes recorded against this interview.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={detailCardSx} mb={2}>
            <Typography fontWeight={700}>{selectedInterviewContext.candidateName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedInterviewContext.flowName} / {selectedInterviewContext.stageName}
            </Typography>
          </Box>
          {historyLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading schedule history...
            </Typography>
          ) : scheduleHistory.length ? (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {scheduleHistory.map((item, index) => {
                const historyStatus = getScheduleStatusChip(item?.schedule_status || item?.status);

                return (
                  <Box
                      key={item?.id || index}
                      sx={{
                        p: 2,
                        borderRadius: "16px",
                        border: `1px solid ${borderColor}`,
                        backgroundColor: surfaceColor,
                      }}
                    >
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={1}>
                      <Chip label={historyStatus.label} color={historyStatus.color} size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {item?.created_at ? moment(item.created_at).format("DD MMM YYYY, hh:mm A") : "History recorded"}
                      </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={0.75}>
                      <Typography variant="body2">
                        <CalendarMonth sx={{ fontSize: "16px", mr: 0.5, mb: "-3px" }} />
                        {item?.datetime ? moment(item.datetime).format("DD MMM YYYY, hh:mm A") : "Date unavailable"}
                      </Typography>
                      <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                        {item?.mode === "offline" ? (
                          <LocationOn sx={{ fontSize: "16px", mr: 0.5, mb: "-3px" }} />
                        ) : (
                          <Videocam sx={{ fontSize: "16px", mr: 0.5, mb: "-3px" }} />
                        )}
                        {item?.mode || "online"} {item?.meeting_link ? `- ${item.meeting_link}` : ""}
                        {item?.address ? `- ${item.address}` : ""}
                      </Typography>
                      {item?.meeting_note ? (
                        <Typography variant="body2" color="text.secondary">
                          Note: {item.meeting_note}
                        </Typography>
                      ) : null}
                      {item?.reason ? (
                        <Typography variant="body2" color="text.secondary">
                          Reason: {item.reason}
                        </Typography>
                      ) : null}
                      {item?.timezone ? (
                        <Typography variant="body2" color="text.secondary">
                          <AccessTime sx={{ fontSize: "16px", mr: 0.5, mb: "-3px" }} />
                          {item.timezone}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No schedule history is available for this interview yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton variant="outlined" color="dark" onClick={closeHistoryDialog}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={feedbackDialog}
        onClose={closeFeedbackDialog}
        PaperProps={{
          sx: {
            width: "480px",
            maxWidth: "95vw",
            borderRadius: "22px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.primary.main} 100%)`,
            color: "white",
          }}
        >
          <Typography variant="h5" color="white">
            Interview Feedback
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.86)" }}>
            Submit the outcome for this round using the documented feedback flow.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={detailCardSx} mb={2}>
            <Typography fontWeight={700}>{selectedInterviewContext.candidateName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedInterviewContext.flowName} / {selectedInterviewContext.stageName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedInterviewRecord?.datetime
                ? moment(selectedInterviewRecord.datetime).format("DD MMM YYYY, hh:mm A")
                : "Schedule unavailable"}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              <Typography fontWeight={500} fontSize="14px" mb={1}>
                Feedback
              </Typography>
              <MDEditor
                value={feedbackForm.feedback || ""}
                onChange={(value) => {
                  setFeedbackForm((state) => ({ ...state, feedback: value }));
                  setFeedbackError("");
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="Evaluated Experience"
              value={feedbackForm.evaluated_exp}
              onChange={(event) => {
                setFeedbackForm((state) => ({ ...state, evaluated_exp: event.target.value }));
                setFeedbackError("");
              }}
            />
            <Autocomplete
              options={feedbackStatuses}
              value={feedbackStatuses.find((option) => option.value === Number(feedbackForm.status)) || null}
              onChange={(event, newValue) => {
                setFeedbackForm((state) => ({
                  ...state,
                  status: newValue?.value || "",
                }));
                setFeedbackError("");
              }}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
            <Box>
              <Typography variant="subtitle2" mb={0.75}>
                Candidate Rating
              </Typography>
              <Rating
                name="interview-rating"
                value={Number(feedbackForm.rating)}
                precision={0.5}
                onChange={(event, newValue) => {
                  setFeedbackForm((state) => ({ ...state, rating: newValue || 0 }));
                  setFeedbackError("");
                }}
              />
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "14px",
                backgroundColor: mutedSurfaceColor,
              }}
            >
              <Typography fontWeight={600} fontSize="14px">
                Valid feedback outcomes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only Selected and Rejected are valid. Cancelled interviews cannot accept feedback.
              </Typography>
            </Box>
            {feedbackError ? (
              <Typography color="error" fontSize="14px">
                {feedbackError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton variant="outlined" color="dark" onClick={closeFeedbackDialog}>
            Close
          </MDButton>
          <MDButton variant="contained" color="info" startIcon={<Send />} onClick={handleSubmitFeedback}>
            Submit Feedback
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Interviews;
